import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@rebass/emotion';
import { getIntlValidLocale } from '@app/helpers/locale';
import { pushData, pushUserData } from '@app/shop/tracking';
import NotFound from '@app/components/NotFound';

import ProductTile from '../components/ProductTile';
import CartErrors from '../components/CartErrors';
import {
  PricingPage_content_pricing_pages,
  PricingPage_content_pricing_pages_layout_item_pricing_pages_multi_layout as MultiLayout,
  PricingPage_content_pricing_pages_layout_item_pricing_pages_multi_layout_products_products_id as ProductId,
} from '../__generated__/PricingPage';

import PricingPageHeader from '../components/PricingPageHeader';
import { BottomContent } from '../SingleProduct/styles';
import messages from '../components/ProductLayout/ProductLayout.messages';

import {
  ContentWrapper,
  ContentTop,
  FindDealerButton,
  ExtraInformation,
} from './styles';
import useLocaleWithFallback from '../../../hooks/useLocaleWithFallback';
import useShopProduct from '../hook/useShopProduct';
export interface MultiProductLayoutProps
  extends Omit<PricingPage_content_pricing_pages, 'layout' | 'translations'> {
  layout: MultiLayout;
}

const DEFAULT_GRID_COLUMNS = 2;

const MultiProductLayout: FC<MultiProductLayoutProps> = ({
  layout,
  title,
  title_color,
}) => {
  const { language } = useParams<{
    language: string;
  }>();
  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();
  const {
    products,
    translations,
    grid_columns,
    show_retailer_button,
    links,
  } = layout;

  const productIds: (ProductId | null | undefined)[] =
    products
      ?.filter((product) => typeof product?.products_id !== 'undefined')
      .map((product) => product?.products_id) || [];

  const { user } = useShopProduct(productIds[0] as ProductId);

  useEffect(() => {
    if (user?.country) {
      pushUserData({
        isAuthenticated: user?.isAuthenticated,
        country: user?.country,
        language,
        pageCategory: 'shop',
      });
      pushData({
        google_tag_params: {
          pagetype: 'product',
          pname: productIds[0]?.title,
          prodid: productIds[0]?.sku,
        },
      });
    }
  }, [user?.country]);

  if (!products) {
    return <NotFound />;
  }

  const contentTop = findTranslationByKeyWithFallback({
    translations,
    key: 'content_top',
  });
  const contentBottom = findTranslationByKeyWithFallback({
    translations,
    key: 'content_bottom',
  });
  const findDealerText = findTranslationByKeyWithFallback({
    translations,
    key: 'find_dealer_text',
  });
  const extraInformation = findTranslationByKeyWithFallback({
    translations,
    key: 'extra_information',
  });

  const headerLinks = links?.map((link) => {
    return {
      title: findTranslationByKeyWithFallback({
        translations: link?.translations,
        key: 'title',
      }),
      url: findTranslationByKeyWithFallback({
        translations: link?.translations,
        key: 'url',
      }),
    };
  });

  return (
    <>
      <Box mb={5} data-testid="pricing_pages_multi_layout">
        <PricingPageHeader
          title={title}
          bgColor={title_color}
          links={headerLinks ?? []}
        />
        <CartErrors />
      </Box>
      <ContentWrapper px={3}>
        {contentTop && (
          <ContentTop mb={4} dangerouslySetInnerHTML={{ __html: contentTop }} />
        )}
        {productIds.length && (
          <Flex flexWrap="wrap">
            {productIds.map((product, idx) =>
              product ? (
                <Box
                  px={3}
                  mb={3}
                  key={idx}
                  width={[1, 1 / (grid_columns ?? DEFAULT_GRID_COLUMNS)]}
                >
                  <ProductTile product={product} />
                </Box>
              ) : null
            )}
          </Flex>
        )}

        {show_retailer_button && (
          <Box textAlign="center">
            {findDealerText && <p>{findDealerText}</p>}
            <FindDealerButton
              href={`/${getIntlValidLocale(language)}/company/resellers`}
            >
              <FormattedMessage {...messages.findDealer} />
            </FindDealerButton>
          </Box>
        )}

        {contentBottom && (
          <BottomContent dangerouslySetInnerHTML={{ __html: contentBottom }} />
        )}

        {extraInformation && (
          <ExtraInformation
            pb={3}
            dangerouslySetInnerHTML={{ __html: extraInformation }}
          />
        )}
      </ContentWrapper>
    </>
  );
};

export default MultiProductLayout;
